import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryResult,
} from '@tanstack/react-query'

import { PaginatedResponse } from '@core/api/dtos/common/types'
import { CreateGroupRequestDTO } from '@core/api/dtos/request/group/create'
import { UpdateGroupRequestDTO } from '@core/api/dtos/request/group/update'
import { UpdateGroupCampaignsRequestDTO } from '@core/api/dtos/request/group/updateCampaigns'
import { UpdateGroupMixesRequestDTO } from '@core/api/dtos/request/group/updateMixes'
import { UpdateGroupPlaylistsRequestDTO } from '@core/api/dtos/request/group/updatePlaylists'
import { UpdateGroupSchedulesRequestDTO } from '@core/api/dtos/request/group/updateSchedules'
import { UpdateGroupZonesRequestDTO } from '@core/api/dtos/request/group/updateZones'
import { Campaign } from '@core/api/entities/campaign/campaign'
import { Group } from '@core/api/entities/group'
import { Playlist } from '@core/api/entities/playlist/playlist'
import { Schedule } from '@core/api/entities/schedule'
import { Zone } from '@core/api/entities/zone/zone'
import { CampaignMapper } from '@core/api/mappers/campaign/campaignMapper'
import { GroupMapper } from '@core/api/mappers/groupMapper'
import { PlaylistMapper } from '@core/api/mappers/playlist/playlistMapper'
import { ScheduleMapper } from '@core/api/mappers/scheduleMapper'
import { ZoneMapper } from '@core/api/mappers/zone/zoneMapper'
import { groupService } from '@core/api/services/groupService'
import { DEFAULT_PARAMS } from '@core/hooks/common'
import { useAccountConfig } from '@core/hooks/useAccountConfig'

/**
 * Get groups
 */
export const useGroups = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<PaginatedResponse<Group>, Error> => {
	const accountConfig = useAccountConfig(config, true)

	const QUERY_KEY = [
		'groups',
		{
			...accountConfig?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			groupService.paginateGroups(accountConfig).then(({ data: response }) => {
				return {
					data: GroupMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		gcTime: 1000 * 60 * 5, // 5 minutes
		staleTime: 1000 * 60, // 1 minute
		...options,
	})
}

/**
 * Get group
 */
export const useGroup = ({
	groupId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Group, Error> => {
	const QUERY_KEY = [
		'group',
		groupId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			groupService.getGroup(groupId, config).then(({ data: response }) => {
				return GroupMapper.toEntity(response.data)
			}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Create group
 */
export const useCreateGroup = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Group,
	Error,
	CreateGroupRequestDTO,
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<Group, Error, CreateGroupRequestDTO, any>({
		mutationFn: (data: CreateGroupRequestDTO) =>
			groupService.createGroup(data, config).then(({ data: response }) => {
				return GroupMapper.toEntity(response.data)
			}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Update group
 */
export const usePatchGroup = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Group,
	Error,
	{ groupId: number; data: UpdateGroupRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		Group,
		Error,
		{ groupId: number; data: UpdateGroupRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupRequestDTO
		}) =>
			groupService
				.patchGroup(groupId, data, config)
				.then(({ data: response }) => {
					return GroupMapper.toEntity(response.data)
				}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Delete group
 */
export const useDeleteGroup = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<void, Error, number, any> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<void, Error, number, any>({
		mutationFn: (groupId: number) => groupService.deleteGroup(groupId, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Fetch group zones
 */
export const useFetchGroupZones = ({
	groupId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Zone[], Error> => {
	const QUERY_KEY = ['group-zones', groupId, { ...config?.params }]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			groupService
				.fetchGroupZones(groupId, config)
				.then(({ data: response }) => {
					return ZoneMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Update group zones
 */
export const useUpdateGroupZones = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Zone[],
	Error,
	{ groupId: number; data: UpdateGroupZonesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		Zone[],
		Error,
		{ groupId: number; data: UpdateGroupZonesRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupZonesRequestDTO
		}) =>
			groupService
				.updateGroupZones(groupId, data, config)
				.then(({ data: response }) => {
					return ZoneMapper.toEntities(response.data)
				}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Assign a zone to multiple groups
 */
export const useAssignZoneToGroups = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; groupIds: number[] },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<void, Error, { zoneId: number; groupIds: number[] }, any>({
		mutationFn: ({
			zoneId,
			groupIds,
		}: {
			zoneId: number
			groupIds: number[]
		}) => {
			const payload: UpdateGroupZonesRequestDTO = { players: [zoneId] }
			return Promise.all(
				groupIds.map((groupId) =>
					groupService.addZoneToGroup(groupId, zoneId, payload, config)
				)
			).then(() => {})
		},
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			queryClient.invalidateQueries({ queryKey: ['group-zones'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Update group campaigns
 */
export const useUpdateGroupCampaigns = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Campaign[],
	Error,
	{ groupId: number; data: UpdateGroupCampaignsRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		Campaign[],
		Error,
		{ groupId: number; data: UpdateGroupCampaignsRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupCampaignsRequestDTO
		}) =>
			groupService
				.updateGroupCampaigns(groupId, data, config)
				.then(({ data: response }) => {
					return CampaignMapper.toEntities(response.data)
				}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Update group mixes
 */
export const useUpdateGroupMixes = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ groupId: number; data: UpdateGroupMixesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ groupId: number; data: UpdateGroupMixesRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupMixesRequestDTO
		}) => groupService.updateGroupMixes(groupId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Get group schedules
 */
export const useGetGroupSchedules = ({
	groupId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Schedule[], Error> => {
	const QUERY_KEY = ['group-schedules', groupId, { ...config?.params }]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			groupService
				.getGroupSchedules(groupId, config)
				.then(({ data: response }) => {
					return ScheduleMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Update group schedules
 */
export const useUpdateGroupSchedules = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ groupId: number; data: UpdateGroupSchedulesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ groupId: number; data: UpdateGroupSchedulesRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupSchedulesRequestDTO
		}) => groupService.updateGroupSchedules(groupId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Add schedules to a group
 */
export const useAddSchedulesToGroup = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ groupId: number; data: UpdateGroupSchedulesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ groupId: number; data: UpdateGroupSchedulesRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupSchedulesRequestDTO
		}) => groupService.addSchedulesToGroup(groupId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			queryClient.invalidateQueries({ queryKey: ['group-schedules'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Remove schedules from a group
 */
export const useRemoveSchedulesFromGroup = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ groupId: number; data: UpdateGroupSchedulesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ groupId: number; data: UpdateGroupSchedulesRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupSchedulesRequestDTO
		}) => groupService.removeSchedulesFromGroup(groupId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			queryClient.invalidateQueries({ queryKey: ['group-schedules'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Get group playlists
 */
export const useGetGroupPlaylists = ({
	groupId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Playlist[], Error> => {
	const QUERY_KEY = ['group-playlists', groupId, { ...config?.params }]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			groupService
				.getGroupPlaylists(groupId, config)
				.then(({ data: response }) => {
					return PlaylistMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Add playlists to a group
 */
export const useAddPlaylistsToGroup = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ groupId: number; data: UpdateGroupPlaylistsRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ groupId: number; data: UpdateGroupPlaylistsRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupPlaylistsRequestDTO
		}) => groupService.addPlaylistsToGroup(groupId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			queryClient.invalidateQueries({ queryKey: ['group-playlists'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Remove playlists from a group
 */
export const useRemovePlaylistsFromGroup = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ groupId: number; data: UpdateGroupPlaylistsRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ groupId: number; data: UpdateGroupPlaylistsRequestDTO },
		any
	>({
		mutationFn: ({
			groupId,
			data,
		}: {
			groupId: number
			data: UpdateGroupPlaylistsRequestDTO
		}) => groupService.removePlaylistsFromGroup(groupId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['groups'] })
			queryClient.invalidateQueries({ queryKey: ['group'] })
			queryClient.invalidateQueries({ queryKey: ['group-playlists'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}
